import React, { useEffect, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { BackPill } from '../BackPill';
import { loadAtlas, BENEFITS, PILLAR, fmt } from './atlasData';

const PAGE = 150;
const COL = { items: PILLAR.fuel, nuts: PILLAR.force, bens: PILLAR.energy };

const ColSearch = ({ value, onChange, color, placeholder, testId }) => (
  <div className="flex items-center gap-1 px-2 h-8 rounded-full mb-1.5 flex-shrink-0"
    style={{ border: `1px solid ${color}66`, background: `${color}0F` }}>
    <Search size={11} color={color}/>
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId}
      className="flex-1 min-w-0 bg-transparent outline-none font-['Outfit'] text-white placeholder-white/30" style={{ fontSize: 11 }}/>
    {value && <button type="button" onClick={() => onChange('')} data-testid={`${testId}-clear`}><X size={11} color={color}/></button>}
  </div>
);

const Row = ({ active, dim, color, onClick, testId, children }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className="w-full text-left px-2 py-1.5 rounded-lg mb-1 transition-colors"
    style={{ background: active ? `${color}26` : 'rgba(255,255,255,0.025)', border: `1px solid ${active ? color : 'rgba(255,255,255,0.06)'}`,
      opacity: dim ? 0.38 : 1, boxShadow: active ? `0 0 10px ${color}40` : 'none' }}>
    {children}
  </button>
);

const Bar = ({ pct, color }) => (
  <div className="h-[3px] rounded-full mt-1 overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
    <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: color }}/>
  </div>
);

const Column = ({ title, color, count, children, search }) => (
  <div className="flex flex-col min-w-0 min-h-0 rounded-2xl p-1.5"
    style={{ border: `1px solid ${color}40`, background: `linear-gradient(180deg, ${color}0D, transparent 40%)` }}>
    <div className="flex items-baseline justify-between px-1 mb-1.5">
      <span className="font-['Cinzel'] uppercase truncate" style={{ fontSize: 10, letterSpacing: '0.16em', color }}>{title}</span>
      <span className="font-['Outfit'] tabular-nums" style={{ fontSize: 8, color: 'rgba(255,255,255,0.4)' }}>{count}</span>
    </div>
    {search}
    <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain pr-0.5">{children}</div>
  </div>
);

/* Nutrient Atlas — Items | Nutrients | Benefits, cross-linked and searchable. */
export const NutrientAtlas = ({ onBack }) => {
  const [atlas, setAtlas] = useState(null);
  const [sel, setSel] = useState(null);
  const [qF, setQF] = useState(''); const [qN, setQN] = useState(''); const [qB, setQB] = useState('');
  const [limit, setLimit] = useState(PAGE);

  useEffect(() => { loadAtlas().then(setAtlas).catch(() => setAtlas({ error: true })); }, []);
  useEffect(() => { setLimit(PAGE); }, [qF, sel]);

  const pick = (type, id) => setSel((s) => (s && s.type === type && s.id === id ? null : { type, id }));
  const food = atlas && sel && sel.type === 'food' ? atlas.foods[sel.id] : null;
  const nut = atlas && sel && sel.type === 'nut' ? atlas.nutrients[atlas.idx[sel.id]] : null;
  const ben = sel && sel.type === 'ben' ? BENEFITS.find((b) => b.id === sel.id) : null;

  const items = useMemo(() => {
    if (!atlas || atlas.error) return [];
    const q = qF.trim().toLowerCase();
    let list = q ? atlas.foods.filter((f) => f.lower.includes(q) || f.group.toLowerCase().includes(q)) : atlas.foods;
    if (nut) { const i = atlas.idx[nut.key]; list = list.filter((f) => f.vals[i] > 0).sort((a, b) => b.vals[i] - a.vals[i]); }
    if (ben) list = list.map((f) => [f, f.benefits.find((b) => b.id === ben.id)]).filter(([, b]) => b).sort((a, b) => b[1].score - a[1].score).map(([f]) => f);
    return list;
  }, [atlas, qF, nut, ben]);

  const nuts = useMemo(() => {
    if (!atlas || atlas.error) return [];
    const q = qN.trim().toLowerCase();
    let list = atlas.nutrients.filter((n) => !q || n.label.toLowerCase().includes(q));
    if (food) list = [...list].sort((a, b) => food.pct[atlas.idx[b.key]] - food.pct[atlas.idx[a.key]] || (b.dv ? 0 : 1) - (a.dv ? 0 : 1));
    if (ben) list = [...list].sort((a, b) => ben.keys.includes(b.key) - ben.keys.includes(a.key));
    return list;
  }, [atlas, qN, food, ben]);

  const bens = useMemo(() => {
    const q = qB.trim().toLowerCase();
    let list = BENEFITS.filter((b) => !q || b.label.toLowerCase().includes(q));
    if (food) { const s = Object.fromEntries(food.benefits.map((b) => [b.id, b.score])); list = [...list].sort((a, b) => (s[b.id] || -1) - (s[a.id] || -1)); }
    if (nut) list = [...list].sort((a, b) => b.keys.includes(nut.key) - a.keys.includes(nut.key));
    return list;
  }, [qB, food, nut]);

  const benOn = (b) => (food ? food.benefits.some((x) => x.id === b.id) : nut ? b.keys.includes(nut.key) : true);
  const nutOn = (n) => (food ? food.vals[atlas.idx[n.key]] > 0 : ben ? ben.keys.includes(n.key) : true);

  return (
    <div className="relative w-full h-full flex flex-col px-2 pt-2" data-testid="aegis-nutrient-atlas"
      style={{ paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 8px)' }}>
      <div className="flex items-center gap-2 mb-2 flex-shrink-0">
        <BackPill onClick={onBack} accent={PILLAR.aegis} testId="aegis-atlas-back"/>
        <div className="flex-1 min-w-0 text-center">
          <p className="font-['Cinzel'] uppercase truncate" style={{ fontSize: 12, letterSpacing: '0.22em',
            background: `linear-gradient(90deg, ${PILLAR.fuel}, ${PILLAR.force}, ${PILLAR.energy})`, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
            Nutrient Atlas
          </p>
          <p className="font-['Cormorant_Garamond'] italic truncate" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }} data-testid="aegis-atlas-selection">
            {food ? `${food.name} · per 100 g` : nut ? `Richest in ${nut.label}` : ben ? `Best for ${ben.label}` : 'Tap any item, nutrient or benefit'}
          </p>
        </div>
        {sel ? <button type="button" onClick={() => setSel(null)} data-testid="aegis-atlas-clear"
          className="h-7 px-2.5 rounded-full font-['Outfit'] uppercase flex-shrink-0" style={{ fontSize: 9, letterSpacing: '0.16em', color: PILLAR.power, border: `1px solid ${PILLAR.power}66` }}>Clear</button>
          : <span className="w-[52px] flex-shrink-0"/>}
      </div>

      {!atlas && <p className="text-center font-['Outfit'] text-white/40 text-xs mt-10" data-testid="aegis-atlas-loading">Loading atlas…</p>}
      {atlas && atlas.error && <p className="text-center font-['Outfit'] text-xs mt-10" style={{ color: PILLAR.power }}>Could not load nutrient data.</p>}
      {atlas && !atlas.error && (
        <div className="flex-1 min-h-0 grid gap-1.5" style={{ gridTemplateColumns: '1.25fr 1fr 1fr' }}>
          <Column title="Items" color={COL.items} count={items.length}
            search={<ColSearch value={qF} onChange={setQF} color={COL.items} placeholder="Spinach, fish…" testId="atlas-search-items"/>}>
            {items.slice(0, limit).map((f) => (
              <Row key={f.id} active={food && food.id === f.id} color={COL.items} onClick={() => pick('food', f.id)} testId={`atlas-item-${f.id}`}>
                <span className="block font-['Outfit'] text-white leading-tight" style={{ fontSize: 11 }}>{f.name}</span>
                <span className="block font-['Outfit'] truncate" style={{ fontSize: 8.5, color: `${COL.items}AA` }}>
                  {nut ? `${fmt(f.vals[atlas.idx[nut.key]])} ${nut.unit}${nut.dv ? ` · ${Math.round(f.pct[atlas.idx[nut.key]])}% DV` : ''}` : `${f.group} · ${Math.round(f.vals[0])} kcal`}
                </span>
              </Row>
            ))}
            {items.length > limit && (
              <button type="button" onClick={() => setLimit((l) => l + PAGE)} data-testid="atlas-items-more"
                className="w-full py-1.5 rounded-lg font-['Outfit'] uppercase" style={{ fontSize: 9, letterSpacing: '0.16em', color: COL.items, border: `1px dashed ${COL.items}66` }}>
                Show more ({items.length - limit})
              </button>
            )}
            {!items.length && <p className="font-['Outfit'] text-white/40 text-center mt-4" style={{ fontSize: 10 }}>No match</p>}
          </Column>

          <Column title="Nutrients" color={COL.nuts} count={nuts.length}
            search={<ColSearch value={qN} onChange={setQN} color={COL.nuts} placeholder="Iron, B12…" testId="atlas-search-nutrients"/>}>
            {nuts.map((n) => {
              const i = atlas.idx[n.key];
              return (
                <Row key={n.key} active={nut && nut.key === n.key} dim={!nutOn(n)} color={n.color} onClick={() => pick('nut', n.key)} testId={`atlas-nutrient-${n.key}`}>
                  <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: n.color, boxShadow: `0 0 6px ${n.color}` }}/>
                    <span className="font-['Outfit'] text-white truncate" style={{ fontSize: 11 }}>{n.label}</span>
                  </span>
                  {food ? (
                    <>
                      <span className="block font-['Outfit'] tabular-nums" style={{ fontSize: 9, color: n.color }}>
                        {fmt(food.vals[i])} {n.unit}{n.dv ? ` · ${Math.round(food.pct[i])}%` : ''}
                      </span>
                      {n.dv ? <Bar pct={food.pct[i]} color={n.color}/> : null}
                    </>
                  ) : (
                    <span className="block font-['Outfit']" style={{ fontSize: 8.5, color: 'rgba(255,255,255,0.4)' }}>{n.dv ? `Daily ${n.dv} ${n.unit}` : 'Macro'}</span>
                  )}
                </Row>
              );
            })}
          </Column>

          <Column title="Benefits" color={COL.bens} count={bens.length}
            search={<ColSearch value={qB} onChange={setQB} color={COL.bens} placeholder="Skin, bone…" testId="atlas-search-benefits"/>}>
            {bens.map((b) => (
              <Row key={b.id} active={ben && ben.id === b.id} dim={!benOn(b)} color={COL.bens} onClick={() => pick('ben', b.id)} testId={`atlas-benefit-${b.id}`}>
                <span className="block font-['Outfit'] text-white leading-tight" style={{ fontSize: 11 }}>{b.label}</span>
                <span className="block font-['Outfit'] leading-tight" style={{ fontSize: 8.5, color: `${COL.bens}CC` }}>
                  {b.keys.map((k) => atlas.nutrients[atlas.idx[k]].label.replace('Vitamin ', 'Vit ')).join(' · ')}
                </span>
              </Row>
            ))}
          </Column>
        </div>
      )}
    </div>
  );
};

export default NutrientAtlas;
